import classNames from 'classnames';
import type { ReactElement, RefObject, Ref } from 'react';
import { useRef } from 'react';
import styles from './OfficeContactInfo.module.scss';
import Heading from '../../atoms/heading/Heading';
import Text from '../../atoms/text/Text';
import { OfficeEmail } from '../location/OfficeEmail';
import type { OfficeContactInfo } from './OfficeDetails.data';

type OfficeContactInfoProps = {
  className?: string;
  office: OfficeContactInfo;
};
type OfficeContactInfoRef = HTMLDivElement;

export default function OfficeContactInfo({
  className,
  office,
  ref,
}: OfficeContactInfoProps & { ref?: Ref<OfficeContactInfoRef> }): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;

  // The `replace('\n', '\n')` line below is to prevent irregular-whitespace character glitch from appearing to the end user
  // https://dentsucreative.monday.com/boards/2362622558/pulses/3475412953?asset_id=657568975
  const address = office.address.replace('\n', '\n').trim().split('\n');

  return (
    <div ref={elementRef} className={classNames(styles.officeContactInfo, className)}>
      {office.location && (
        <Heading
          as="h5"
          size="heading-05"
          disableTriggerInOnScroll={false}
          className={styles.location}
          transitionInType="splitWordsSlideInUp"
        >
          {office.location}
        </Heading>
      )}

      {office.address && (
        <ul className={classNames(styles.address)}>
          {address.map((split: string) => (
            <Text
              key={split}
              as="li"
              size="body-standard"
              disableTriggerInOnScroll={false}
              transitionInType="splitWordsSlideInUp"
              transitionOutType="fadeOut"
            >
              {split}
            </Text>
          ))}
        </ul>
      )}

      <ul>
        {office.emails.map((email) => (
          <OfficeEmail email={email} key={email._uid} />
        ))}
      </ul>
    </div>
  );
}
